/**
 * Thin fetch wrapper. All API calls go to /api/..., which Vite proxies in dev
 * and Caddy proxies in production, so the browser only ever talks to one origin.
 * Sessions ride on an httpOnly cookie; nothing auth-related lives in JS.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

export async function api<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, headers, body, ...rest } = init ?? {}
  const res = await fetch(`/api${path}`, {
    ...rest,
    headers: { ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...(headers as Record<string, string> | undefined) },
    ...(json !== undefined ? { body: JSON.stringify(json) } : body !== undefined ? { body } : {}),
    credentials: 'same-origin',
  })
  if (!res.ok) {
    let message = `${rest.method ?? 'GET'} ${path} → ${res.status}`
    try {
      const body = (await res.json()) as { message?: string }
      if (body.message) message = body.message
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, message)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export interface Health {
  status: 'ok'
  db: 'ok'
  migrations: number
  uptimeSeconds: number
}

export type Role = 'admin' | 'manager' | 'member' | 'viewer'

export interface User {
  id: string
  email: string
  displayName: string
  role: Role
  timezone: string
  locale: string
  avatarUrl: string | null
  jobTitle: string | null
  githubLogin: string | null
  managerId: string | null
  hasPassword: boolean
  hasGoogle: boolean
  totpEnabled: boolean
  ipRestricted: boolean
}

export interface Me { user: User | null; googleEnabled: boolean; mfa: { required: boolean; recoveryCodesLeft: number; passkeys: number } | null }
export interface MfaStatus {
  totpEnabled: boolean; recoveryCodesLeft: number; required: boolean
  passkeys: { id: string; name: string; createdAt: string; lastUsedAt: string | null; backedUp: boolean }[]
  ipRestricted: boolean; allowlist: { cidr: string; label: string | null }[]
}

export interface DirectoryUser {
  id: string; email: string; displayName: string; role: Role; timezone: string; locale: string
  jobTitle: string | null; githubLogin: string | null; avatarUrl: string | null; managerId: string | null; weeklyCapacityHours: number
  serviceAccount: boolean; workingDaysPerWeek: number | null
  deactivatedAt: string | null; lastLoginAt: string | null; createdAt: string
}

export interface Invite {
  id: string; email: string; role: Role; createdAt: string; expiresAt: string; acceptedAt: string | null
}

export interface SessionInfo {
  id: string; createdAt: string; lastSeenAt: string; expiresAt: string
  ip: string | null; userAgent: string | null
}

export const browserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}

export type ProjectStatus = 'not_started' | 'active' | 'on_hold' | 'done' | 'archived'
export type ProjectRole = 'admin' | 'member' | 'viewer'

export interface Project {
  id: string; code: string; name: string; description: string | null
  timezoneOfTruth: string; status: ProjectStatus
  startsOn: string | null; endsOn: string | null
  portfolioId: string | null; ownerId: string | null
  archivedAt: string | null; createdAt: string; updatedAt: string
  memberCount: number; myRole: ProjectRole | null
  isPrivate: boolean; position: number; color: string | null
}
export interface ProjectMember { userId: string; role: ProjectRole; displayName: string; email: string; avatarUrl: string | null; addedAt: string }
export interface TaskStatus { id: string; name: string; category: 'todo' | 'in_progress' | 'done' | 'archived'; position: number; wipLimit: number | null; color: string | null; automation: { assigneeId?: string | null; clearAssignee?: boolean; priority?: string | null; dueInDays?: number | null } }
export interface Milestone { id: string; name: string; description: string | null; dueOn: string | null; completedAt: string | null; position: number }
export interface ProjectDetail {
  project: Project; members: ProjectMember[]; statuses: TaskStatus[]; milestones: Milestone[]
  permissions: { update: boolean; archive: boolean; manageMembers: boolean; manageStatuses: boolean; manageMilestones: boolean; logTime: boolean }
}
export interface GithubConnection {
  owner: string; repo: string; htmlUrl: string; tokenLast4: string; pushStatus: boolean; syncLabels: boolean
  doneStatusId: string | null; notPlannedStatusId: string | null; reopenStatusId: string | null
  priorityLabels: Record<string, Priority>; lastPollAt: string | null; disabledReason: string | null
  createdAt: string; updatedAt: string; pendingUpdates: number; linkedIssues: number
}
export interface GithubIssueRow {
  number: number; title: string; state: 'open' | 'closed'; stateReason: string | null; url: string; labels: string[]; assignees: string[]; author: string | null
  milestone: string | null; updatedAt: string; action: 'create' | 'linked' | 'update'; taskId: string | null; taskKey: string | null
  priority: Priority | null; assigneeId: string | null; assigneeName: string | null
}
export interface GithubStatus { connected: boolean; connection: GithubConnection | null; webhookConfigured: boolean; apiUrl: string }
export interface Portfolio {
  id: string; name: string; description: string | null; archivedAt: string | null; projectCount: number
  ownerId: string | null; ownerName: string | null; myRole: ProjectRole | null; createdAt: string; updatedAt: string
}
export interface PortfolioProject {
  id: string; code: string; name: string; description: string | null; status: ProjectStatus
  startsOn: string | null; endsOn: string | null; archivedAt: string | null
  ownerId: string | null; isPrivate: boolean; position: number; color: string | null
  timezoneOfTruth: string; autoSchedule: boolean; myRole: ProjectRole | null; memberCount: number
  statuses: TaskStatus[]; milestones: Milestone[]
  permissions: { manageTasks: boolean; update: boolean; manageStatuses: boolean; manageMilestones: boolean; readAllTime: boolean }
}
export interface PortfolioDetail {
  portfolio: Portfolio; members: ProjectMember[]; projects: PortfolioProject[]; hiddenProjects: number
  permissions: { update: boolean; manageMembers: boolean; manageProjects: boolean }
}
export interface PortfolioCounters { total: number; todo: number; inProgress: number; done: number; overdue: number; dueSoon: number; unassigned: number }
export interface PortfolioOverview {
  projects: {
    id: string; code: string; name: string; color: string | null; status: ProjectStatus; startsOn: string | null; endsOn: string | null
    tasks: PortfolioCounters; hours: { estimated: number; logged: number; logged30d: number }
    milestones: { total: number; done: number; overdue: number; next: { id: string; name: string; dueOn: string } | null }
    autoSchedule: boolean; scheduleEnd: string | null; canSeeAllTime: boolean
  }[]
  totals: { tasks: PortfolioCounters; hours: { estimated: number; logged: number; logged30d: number }; milestones: { total: number; done: number; overdue: number } }
  portfolioEnd: string | null; criticalCount: number
  upcomingMilestones: { id: string; projectId: string; projectCode: string; name: string; dueOn: string; overdue: boolean }[]
  hiddenProjects: number
}

/**
 * Worked example for the timezone-of-truth confirmation (§3): the next Monday
 * 00:00 in `zone`, with the zone's abbreviation, so the person creating the
 * project sees concretely when weeks will close. Returns ok:false for a zone
 * Intl does not know.
 */
export function weekCloseExample(zone: string): { ok: true; text: string } | { ok: false } {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric', timeZoneName: 'short' })
    for (let d = 1; d <= 8; d++) {
      const probe = new Date(Date.now() + d * 86_400_000)
      const parts = fmt.formatToParts(probe)
      const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
      if (get('weekday') === 'Mon') return { ok: true, text: `Mon ${get('month')} ${get('day')}, 00:00 ${get('timeZoneName')}` }
    }
    return { ok: false }
  } catch { return { ok: false } }
}

export interface TimeEntry {
  id: string; userId: string; projectId: string; projectCode: string; projectName: string
  taskId: string | null; taskTitle: string | null
  startedAt: string; endedAt: string; durationMinutes: number
  userTz: string; userLocalDate: string; projectWeekStart: string
  billable: boolean; notes: string | null; correctsEntryId: string | null; createdBy: string | null
  createdAt: string; updatedAt: string
}
export interface Overlap { id: string; projectCode: string; startedAt: string; endedAt: string }

export type Priority = 'low' | 'medium' | 'high' | 'urgent'
export interface Task {
  id: string; projectId: string; projectCode: string; projectName: string; number: number; key: string
  parentId: string | null; depth: number; path: string[]
  title: string; description: string | null
  statusId: string; statusName: string; statusCategory: 'todo' | 'in_progress' | 'done' | 'archived'
  assigneeId: string | null; assigneeName: string | null
  milestoneId: string | null; priority: Priority | null
  startsOn: string | null; dueOn: string | null; completedAt: string | null
  estimateHours: number | null; position: number; boardRank: number
  loggedMinutes: number; subtreeLoggedMinutes: number; subtreeEstimateHours: number
  childCount: number; subtreeDoneCount: number; subtreeActiveCount: number; subtreeCount: number
  createdBy: string | null; createdAt: string; updatedAt: string; statusChangedAt: string
  recurrence: { freq: 'daily' | 'weekly' | 'monthly'; interval: number } | null; recurrenceOf: string | null
  kind: 'task' | 'checklist'; checklistTotal: number; checklistDone: number
}
/**
 * Comments (APM-7). One shape behind three entry points — a task, a project's
 * own discussion, a milestone. Threads are one level deep: only roots come
 * back, each carrying its replies in time order. Mentions live inside `body`
 * as `@[Display Name](uuid)`; readers are shown a chip, never the markup.
 */
export interface CommentMention { userId: string; displayName: string }
export interface Comment {
  id: number; parentId: number | null; authorId: string | null; authorName: string | null
  body: string; system: boolean; createdAt: string; editedAt: string | null
  deleted: boolean; deletedByName: string | null
  mentions: CommentMention[]; canEdit: boolean; canDelete: boolean
}
export interface CommentThread extends Comment { replies: Comment[] }
export interface CommentRevision { id: number; body: string; editedAt: string; editedByName: string | null }
/**
 * Attachments: files on a task, project or milestone, and the ones embedded in
 * a comment (commentId set). `url` already carries the /api prefix, so it goes
 * straight into an <img> or <a>.
 */
export interface Attachment {
  id: string; entityType: 'task' | 'project' | 'milestone'; entityId: string; commentId: number | null
  fileName: string; contentType: string; sizeBytes: number; sha256: string
  uploadedById: string | null; uploadedByName: string | null; createdAt: string
  url: string; image: boolean; preview: 'image' | 'pdf' | 'text' | null; canDelete: boolean
}
/** Upload one file: the raw bytes as the body, the name in the query. */
export const uploadFile = (path: string, file: File) =>
  api<{ attachment: Attachment }>(`${path}?name=${encodeURIComponent(file.name || 'pasted-file')}`, {
    method: 'POST', body: file, headers: { 'content-type': file.type || 'application/octet-stream' },
  }).then((r) => r.attachment)
export interface TaskTransition {
  id: number; fromStatusId: string | null; toStatusId: string; fromCategory: string | null; toCategory: string
  changedAt: string; changedBy: string; changedByName: string
}
export interface TaskDetail { task: Task; ancestors: { id: string; key: string; title: string }[]; transitions: TaskTransition[] }

export interface AuditRow {
  id: number; at: string; actorId: string | null; actorName: string | null; via: string | null; viaName: string | null
  action: 'insert' | 'update' | 'delete'; entityType: string; entityId: string
  projectId: string | null; projectCode: string | null; label: string | null
  before: Record<string, unknown> | null; after: Record<string, unknown> | null; changed: string[] | null
}
export interface AuditPage { rows: AuditRow[]; nextBefore: number | null }
export interface ApiToken { id: string; name: string; scope: 'read' | 'write'; userId: string; ownerName: string | null; createdAt: string; lastUsedAt: string | null; expiresAt: string | null; revokedAt: string | null }
export interface AdminStats {
  users: { active: number; deactivated: number; admins: number; withGoogle: number; withPassword: number }
  projects: { active: number; archived: number }
  tasks: number; timeEntries: number; loggedMinutes30d: number
  sessionsActive: number; pendingInvites: number; auditRows: number; migrations: number; failedLogins24h: number
}
export interface LoginAttempt { id: number; at: string; email: string; ip: string | null; succeeded: boolean; reason: string | null }

export interface Dependency { predecessorId: string; successorId: string; type: 'FS' | 'SS' | 'FF' | 'SF'; lagDays: number; predecessorProjectId: string; successorProjectId: string }

export interface WeekPoint { weekStart: string; minutes: number }
export interface HomeDashboard {
  week: { from: string; to: string; minutes: number; previousMinutes: number; byProject: { projectId: string; code: string; name: string; minutes: number }[] }
  weekly: WeekPoint[]
  myTasks: { id: string; projectId: string; key: string; title: string; statusName: string; statusCategory: string; dueOn: string | null; priority: string | null; overdue: boolean }[]
  projects: { id: string; code: string; name: string; myRole: string | null; tasksTotal: number; tasksDone: number; minutes30d: number; openMine: number }[]
  outstanding: { projectId: string; code: string; name: string; timezoneOfTruth: string; weekStart: string; minutes: number; state: string }[]
  insufficientHistory: boolean
}
export interface ProjectDashboard {
  tasks: { total: number; todo: number; inProgress: number; done: number; overdue: number; dueSoon: number; unassigned: number }
  hours: { estimated: number; logged: number; logged30d: number; billableShare: number | null }
  milestones: { id: string; name: string; dueOn: string | null; completedAt: string | null; overdue: boolean; tasksTotal: number; tasksDone: number }[]
  members: { userId: string; displayName: string; role: string; minutes30d: number; openTasks: number }[]
  weekly: WeekPoint[]
  insufficientHistory: boolean
  canSeeAllTime: boolean
}

export type ViewKind = 'list' | 'board' | 'gantt' | 'calendar' | 'time'
export interface SavedView { id: string; projectId: string | null; userId: string; name: string; kind: ViewKind; config: Record<string, unknown>; isShared: boolean; mine: boolean; createdAt: string; updatedAt: string }
export interface SearchResults {
  tasks: { id: string; projectId: string; projectCode: string; key: string; title: string; statusName: string; statusCategory: string; assigneeName: string | null; dueOn: string | null }[]
  projects: { id: string; code: string; name: string; status: string }[]
  people: { id: string; displayName: string; email: string; role: string }[]
}

export interface RunningTimer {
  projectId: string; projectCode: string; projectName: string; taskId: string | null; taskKey: string | null; taskTitle: string | null
  notes: string | null; billable: boolean; running: boolean; runningSince: string | null; accumulatedSeconds: number; elapsedSeconds: number
  firstStartedAt: string; lastSeenAt: string
}
export type ProjectTimer = RunningTimer

export type ApprovalState = 'open' | 'submitted' | 'approved' | 'rejected'
export interface ApprovalPeriod {
  userId: string; userName: string; projectId: string; projectCode: string; projectName: string; timezoneOfTruth: string
  weekStart: string; state: ApprovalState; minutes: number; entryCount: number; capacityHours: number
  submittedAt: string | null; submitNote: string | null; decidedAt: string | null; decidedByName: string | null
  decisionNote: string | null; forceApproved: boolean; isCurrentWeek: boolean
}
export interface ApprovalWarnings { empty: boolean; underCapacity: boolean; minutes: number; capacityHours: number; unsubmitted: boolean }
export interface Notification { id: number; kind: string; title: string; body: string | null; link: string | null; createdAt: string; readAt: string | null }

export interface Rate { id: string; userId: string; userName: string; projectId: string | null; effectiveFrom: string; amount: number; currency: string; createdAt: string; current: boolean }
export interface Budget {
  budgetHours: number | null; budgetAmount: number | null; budgetCurrency: string | null; alertPct: number
  consumedHours: number; consumedAmount: number | null; costAmount: number | null; unratedMinutes: number | null
  hoursPct: number | null; amountPct: number | null; canSeeMoney: boolean
}

export interface LeaveType { id: string; code: string; name: string; reducesCapacity: boolean; requiresApproval: boolean; color: string | null; archivedAt: string | null }
export interface LeaveRequest {
  id: string; userId: string; userName: string; typeId: string; typeName: string; startsOn: string; endsOn: string
  hoursPerDay: number | null; hours: number; note: string | null; state: 'submitted' | 'approved' | 'rejected' | 'cancelled'
  submittedAt: string; decidedAt: string | null; decidedByName: string | null; decisionNote: string | null; managerId: string | null
}
export interface PayrollPerson { userId: string; name: string; email: string; totalMinutes: number; approvedMinutes: number; outstanding: { projectCode: string; weekStart: string; state: string }[]; leaveHours: number; ready: boolean }

export interface ScheduleRow { taskId: string; projectId: string; es: string | null; ef: string | null; ls: string | null; lf: string | null; slackDays: number | null; critical: boolean; durationDays: number | null }
export interface Schedule {
  projectEnd: string | null; criticalIds: string[]; rows: ScheduleRow[]
  baseline: { takenAt: string; takenByName: string | null; tasks: { taskId: string; startsOn: string | null; dueOn: string | null; estimateHours: number | null }[] } | null
  autoSchedule: boolean
}
export interface PortfolioSchedule {
  portfolioEnd: string | null; criticalIds: string[]; rows: ScheduleRow[]
  projects: { projectId: string; projectEnd: string | null; autoSchedule: boolean; baseline: Schedule['baseline'] }[]
  hiddenProjects: number
}

export interface Analytics {
  from: string; to: string; timezone: string
  cumulativeFlow: { day: string; todo: number; inProgress: number; done: number }[]
  cycleTime: { count: number; medianDays: number | null; p85Days: number | null; meanDays: number | null; leadMedianDays: number | null; recent: { taskId: string; key: string; title: string; cycleDays: number | null; leadDays: number; doneAt: string }[] }
  burn: { day: string; remainingHours: number; doneHours: number; scopeHours: number; remainingTasks: number; doneTasks: number; scopeTasks: number }[]
  velocity: { weekStart: string; doneHours: number; doneTasks: number }[]
  forecast: { remainingHours: number; remainingTasks: number; avgVelocityHours: number | null; avgThroughput: number | null; projectedFinishByHours: string | null; projectedFinishByCount: string | null; weeksSampled: number }
  insufficientHistory: boolean
}

export interface ReportSchedule { id: string; name: string; config: Record<string, unknown>; format: string; cadence: string; dayOfWeek: number | null; dayOfMonth: number | null; hour: number; timezone: string; recipients: string[]; enabled: boolean; lastRunAt: string | null; nextRunAt: string }
